/**
 * v0.4 追加要件⑪ 章⑥ (D-349/D-361/D-393) + 追加要件⑮ 実装順序④ (D-519/D-535/D-536):
 * 36協定 遵守状況 (`/shift/report/compliance`)。
 * 行=従業員 / 列=[月間法定外・年間法定外・深夜・休日労働]×[実績/見込み]・協定上限・状態。
 * 7列のため <768px でもドリルせずカード縦積みにする (U-10)。
 *
 * ⚠️ D-535: 「実績」(基準時点までに実際に働いた分) と「見込み」(実績+残りの予定) は必ず
 * 別フィールドで並べて表示する (丸めて1つの数値にしない — 賃金の根拠には実績が要り、
 * 36協定の上限管理には見込みが要るため)。D-536: 基準時点 (as_of) は目立つ位置に置く。
 *
 * 設計: docs/design/v04-cross-employee-shift-report-detail.md §7-5 / §11、
 *       docs/design/v04-planned-vs-actual-separation-detail.md §3-3/§4/§6 手順④
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock } from 'lucide-react';
import { MonthPicker } from '@/components/MonthPicker';
import { apiFetch, ApiClientError } from '@/lib/api';
import { useLabel } from '@/lib/labels';
import { EmptyState } from '@/components/EmptyState';
import { StoreMultiSelect, collectStoreIds } from '@/components/StoreMultiSelect';
import { useScopeStore, findNodeInTree } from '@/stores/scope';
import { thisMonthStart } from '@/lib/shift-report-dates';
import { type ViewColumn, formatViewColumnValue, resolveViewColumnLabel } from '@/lib/shift-report-view-columns';
import { OMITTED_REASON_LABELS } from '@/lib/shift-report-labels';

type UndeterminableReason = 'variable_working_hours' | 'no_agreement_36' | 'no_attendance_settings';
type ComplianceStatus = 'ok' | 'approaching' | 'over_limit' | 'undeterminable';

type AgreementInfo = {
  pattern_id: string; pattern_name: string; applied_pattern_version: number;
  valid_from: string; valid_to: string;
  limit_minutes_day: number | null; limit_minutes_month: number | null; limit_minutes_year: number | null;
  has_special_clause: boolean;
  special_max_minutes_per_month: number | null; special_max_minutes_per_year: number | null;
  special_max_months_per_year: number | null; special_months_used: number;
};

type OvertimeValues = {
  overtime_minutes_month: number; overtime_minutes_year: number;
  night_minutes_month: number; holiday_work_minutes_month: number;
  holiday_work_determinable: boolean;
};

type ComplianceRow = {
  employee_id: string | null; external_person_id: string | null;
  display_name: string; home_node_id: string;
  determinable: boolean; undeterminable_reason: UndeterminableReason | null;
  actual?: OvertimeValues;
  forecast?: OvertimeValues;
  agreement?: AgreementInfo;
  status: ComplianceStatus;
  violation_ids?: string[];
};

type ComplianceResponse = {
  month: string;
  month_window_basis: 'calendar_month';
  /** D-519/D-536: サーバが計算した基準時点。クライアントの時計を使わない。 */
  as_of: string;
  scope_summary: { node_count: number; person_count: number };
  // v0.4 追加要件⑪ 章⑦フォローアップ (D-348): /settings/report-views の既定ビューに列が
  // 1つでも設定されていれば、既存の固定7列の右に追加表示する (null = 追加列なし)。
  // determinable=false の行は対象に含まれない (routes/shift-report.ts、法定外労働時間が
  // 定義できないため)。
  view_columns: ViewColumn[] | null;
  rows: ComplianceRow[];
};

/** D-536: 基準時点をブラウザのローカルタイムゾーンで人間可読な形にする。 */
const formatAsOf = (iso: string): string => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** routes/shift-report.ts の targetKey 規約 (employeeId ?? externalPersonId ?? displayName) と一致させる */
const viewColumnTargetKeyOf = (row: ComplianceRow): string => row.employee_id ?? row.external_person_id ?? row.display_name;

const UNDETERMINABLE_REASON_LABELS: Record<UndeterminableReason, string> = {
  variable_working_hours: '変形労働時間制のため判定不能',
  no_agreement_36: '36協定が未登録のため判定不能',
  no_attendance_settings: '勤怠設定が未登録のため判定不能',
};

const STATUS_LABELS: Record<ComplianceStatus, string> = {
  ok: '順調', approaching: '上限に接近', over_limit: '上限超過', undeterminable: '判定不能',
};

// ⚠️ ui-guidelines §2.2「赤は1画面1箇所」: 複数行が同時に over_limit になりうるため
// text-brand-red は使わず text-alert-text (既存の警告トーン、ShiftCoordinationPage 等の前例) を使う。
const STATUS_CLASSES: Record<ComplianceStatus, string> = {
  ok: 'text-text-body',
  approaching: 'text-alert-text',
  over_limit: 'text-alert-text font-bold',
  undeterminable: 'text-text-sub',
};

const fmtMinutes = (m: number | undefined): string => (m === undefined ? '—' : `${Math.floor(m / 60)}時間${m % 60}分`);

/** design-expert Medium指摘: 状態表示が色+テキストのみで、姉妹画面 (ShiftBoardPage.tsx) が
 *  警告に一貫して付けているアイコンが無かった。approaching/over_limit にアイコンを併記する。 */
function StatusBadge({ status }: { status: ComplianceStatus }) {
  const showIcon = status === 'approaching' || status === 'over_limit';
  return (
    <span className={`inline-flex items-center gap-1 ${STATUS_CLASSES[status]}`}>
      {showIcon && <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      {STATUS_LABELS[status]}
    </span>
  );
}

function AgreementCell({ agreement }: { agreement: AgreementInfo | undefined }) {
  if (!agreement) return <span className="text-text-sub" title="協定情報の閲覧権限がありません">—</span>;
  return (
    <div className="text-xs">
      <div>{agreement.pattern_name}</div>
      <div className="text-text-sub">
        月{agreement.limit_minutes_month !== null ? fmtMinutes(agreement.limit_minutes_month) : '未設定'}
        {agreement.has_special_clause && ` (特別条項 ${agreement.special_months_used}/${agreement.special_max_months_per_year ?? '—'}ヶ月)`}
      </div>
    </div>
  );
}

export function ShiftReportCompliancePage() {
  const t = useLabel();
  const scopeRoots = useScopeStore((s) => s.roots);
  const scopeSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const scopedNodes = scopeSelectedNodeId ? (findNodeInTree(scopeRoots, scopeSelectedNodeId) ? [findNodeInTree(scopeRoots, scopeSelectedNodeId)!] : scopeRoots) : scopeRoots;

  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [month, setMonth] = useState(thisMonthStart().slice(0, 7));

  const nodeColorIndex = new Map<string, number>();
  scopedNodes.flatMap(collectStoreIds).forEach((id, idx) => nodeColorIndex.set(id, idx));
  const COLORS = ['#C91F28', '#F05A28', '#F2B705', '#6E80A8', '#1A1A1A', '#8D8D8D'];
  const colorOf = (storeId: string) => COLORS[(nodeColorIndex.get(storeId) ?? 0) % COLORS.length]!;

  const nodeIdsParam = selectedNodeIds.size > 0 ? [...selectedNodeIds].join(',') : null;
  const { data, isLoading, isError, error, refetch } = useQuery<ComplianceResponse>({
    queryKey: ['shift-report-compliance', nodeIdsParam, month],
    queryFn: () => apiFetch<ComplianceResponse>(
      `/shift-report/compliance?month=${month}${nodeIdsParam ? `&node_ids=${encodeURIComponent(nodeIdsParam)}` : ''}`,
    ),
  });

  const outOfScope = isError && error instanceof ApiClientError && error.code === 'OUT_OF_SCOPE';
  const rows = data?.rows ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-h1">{t('entity.shift_report_compliance', '36協定 遵守状況')}</h1>
      </div>

      {/* D-536: 基準時点は目立つ位置に置く (表の隅の小さい注記にしない)。「見込み」は
          この時点までの実績+残りの予定であり、この日時無しでは数字の意味が読めない。 */}
      {data && (
        <div className="flex items-center gap-2 rounded-lg border border-surface-border bg-surface-sunken px-4 py-2.5 text-sm">
          <Clock className="h-4 w-4 shrink-0 text-text-sub" aria-hidden="true" />
          <span className="text-text-body font-medium">基準時点: {formatAsOf(data.as_of)}</span>
          <span className="text-text-sub">この時点までの実績と、残りの予定を含めた見込みを併記しています</span>
        </div>
      )}

      <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="src-month">対象月</label>
            <MonthPicker
              id="src-month" aria-label="対象月" value={month} onChange={setMonth}
              className="min-h-[44px] px-3 py-2 rounded border border-surface-border text-sm"
            />
          </div>
          <div>
            <span className="block text-xs text-text-sub mb-1">対象店舗</span>
            <StoreMultiSelect nodes={scopedNodes} selectedIds={selectedNodeIds} onChange={setSelectedNodeIds} colorOf={colorOf} savedViewNamespace="shift-report-compliance" />
          </div>
        </div>
        {data && (
          <div className="text-xs text-text-sub" aria-live="polite">
            対象店舗 {data.scope_summary.node_count} / 対象者 {data.scope_summary.person_count}名 ・ 暦月で集計しています
          </div>
        )}
        <p className="text-xs text-text-sub">
          本表示は設定された協定上限とシフト予定に基づく参考値です。法令上の判断・賃金の計算は行っていません。
        </p>
      </div>

      {isLoading && <div className="py-10 text-center text-sm text-text-sub" aria-live="polite">読み込み中…</div>}

      {isError && !outOfScope && (
        <EmptyState message="36協定 遵守状況の取得に失敗しました" description={error instanceof ApiClientError ? error.message : undefined} actionLabel="再読み込み" onAction={() => refetch()} />
      )}
      {isError && outOfScope && (
        <EmptyState message="閲覧可能な範囲がありません" description={error instanceof ApiClientError ? error.message : undefined} />
      )}

      {data && rows.length === 0 && !isError && <EmptyState message="対象者がいません" description="対象月に勤務している従業員がいません。月・店舗を見直してください" />}

      {data && rows.length > 0 && (
        <>
          {/* ≥768px: 行=従業員 / 列=[月間法定外・年間法定外・深夜・休日労働]×[実績/見込み]・協定上限・状態 (U-10)。
              D-535: 実績/見込みは列グループとして並べ、丸めて1数値にしない。 */}
          <div className="hidden md:block border border-surface-border rounded-lg bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="border-collapse w-full text-sm">
                <thead>
                  <tr>
                    <th rowSpan={2} scope="col" className="sticky left-0 z-10 bg-surface-sunken border-b border-r border-surface-border px-3 py-2 text-left text-xs text-text-sub align-bottom" style={{ minWidth: 160 }}>氏名</th>
                    <th colSpan={4} scope="colgroup" className="border-b border-l border-surface-border px-2 py-1 text-center text-xs font-semibold text-text-body whitespace-nowrap">実績</th>
                    <th colSpan={4} scope="colgroup" className="border-b border-l border-surface-border px-2 py-1 text-center text-xs font-semibold text-text-body whitespace-nowrap">見込み</th>
                    <th rowSpan={2} scope="col" className="border-b border-l border-surface-border px-2 py-2 text-left text-xs whitespace-nowrap align-bottom">{t('field.shift_report_compliance.agreement', '協定上限')}</th>
                    <th rowSpan={2} scope="col" className="border-b border-l border-surface-border px-2 py-2 text-center text-xs whitespace-nowrap align-bottom">{t('field.shift_report_compliance.status', '状態')}</th>
                    {(data.view_columns ?? []).map((col) => (
                      <th key={col.column_id} rowSpan={2} scope="col" className="border-b border-l border-surface-border px-2 py-2 text-center text-xs whitespace-nowrap align-bottom">
                        {resolveViewColumnLabel(t, col)}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <th scope="col" className="border-b border-l border-surface-border px-2 py-1 text-center text-xs whitespace-nowrap">{t('field.shift_report_compliance.overtime_minutes_month', '月間法定外')}</th>
                    <th scope="col" className="border-b border-surface-border px-2 py-1 text-center text-xs whitespace-nowrap">{t('field.shift_report_compliance.overtime_minutes_year', '年間法定外')}</th>
                    <th scope="col" className="border-b border-surface-border px-2 py-1 text-center text-xs whitespace-nowrap">{t('field.shift_report_compliance.night_minutes_month', '深夜労働')}</th>
                    <th scope="col" className="border-b border-surface-border px-2 py-1 text-center text-xs whitespace-nowrap">{t('field.shift_report_compliance.holiday_work_minutes_month', '法定休日労働')}</th>
                    <th scope="col" className="border-b border-l border-surface-border px-2 py-1 text-center text-xs whitespace-nowrap">{t('field.shift_report_compliance.overtime_minutes_month', '月間法定外')}</th>
                    <th scope="col" className="border-b border-surface-border px-2 py-1 text-center text-xs whitespace-nowrap">{t('field.shift_report_compliance.overtime_minutes_year', '年間法定外')}</th>
                    <th scope="col" className="border-b border-surface-border px-2 py-1 text-center text-xs whitespace-nowrap">{t('field.shift_report_compliance.night_minutes_month', '深夜労働')}</th>
                    <th scope="col" className="border-b border-surface-border px-2 py-1 text-center text-xs whitespace-nowrap">{t('field.shift_report_compliance.holiday_work_minutes_month', '法定休日労働')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const key = row.employee_id ?? row.external_person_id ?? '';
                    return (
                      <tr key={key} className="odd:bg-white even:bg-surface-stripe">
                        <th scope="row" className="sticky left-0 z-10 bg-inherit border-r border-surface-border px-3 py-1.5 text-left text-xs font-medium whitespace-nowrap">{row.display_name}</th>
                        {row.determinable && row.actual && row.forecast ? (
                          <>
                            <td className="border-l border-surface-border px-2 py-1.5 text-center text-xs">{fmtMinutes(row.actual.overtime_minutes_month)}</td>
                            <td className="px-2 py-1.5 text-center text-xs">{fmtMinutes(row.actual.overtime_minutes_year)}</td>
                            <td className="px-2 py-1.5 text-center text-xs">{fmtMinutes(row.actual.night_minutes_month)}</td>
                            <td className="px-2 py-1.5 text-center text-xs">
                              {row.actual.holiday_work_determinable === false ? <span className="text-text-sub" title="法定休日を特定していないため判定不能">未特定</span> : fmtMinutes(row.actual.holiday_work_minutes_month)}
                            </td>
                            <td className="border-l border-surface-border px-2 py-1.5 text-center text-xs">{fmtMinutes(row.forecast.overtime_minutes_month)}</td>
                            <td className="px-2 py-1.5 text-center text-xs">{fmtMinutes(row.forecast.overtime_minutes_year)}</td>
                            <td className="px-2 py-1.5 text-center text-xs">{fmtMinutes(row.forecast.night_minutes_month)}</td>
                            <td className="px-2 py-1.5 text-center text-xs">
                              {row.forecast.holiday_work_determinable === false ? <span className="text-text-sub" title="法定休日を特定していないため判定不能">未特定</span> : fmtMinutes(row.forecast.holiday_work_minutes_month)}
                            </td>
                            <td className="border-l border-surface-border px-2 py-1.5"><AgreementCell agreement={row.agreement} /></td>
                          </>
                        ) : (
                          <td colSpan={9} className="border-l border-surface-border px-2 py-1.5 text-center text-xs text-text-sub">
                            {UNDETERMINABLE_REASON_LABELS[row.undeterminable_reason ?? 'no_attendance_settings']}
                          </td>
                        )}
                        <td className="border-l border-surface-border px-2 py-1.5 text-center text-xs whitespace-nowrap"><StatusBadge status={row.status} /></td>
                        {(data.view_columns ?? []).map((col) => (
                          <td key={col.column_id} className="border-l border-surface-border px-2 py-1.5 text-center text-xs whitespace-nowrap">
                            {!row.determinable
                              ? <span className="text-text-sub">—</span>
                              : col.omitted
                                ? <span className="text-text-sub" title={col.omitted_reason ? (OMITTED_REASON_LABELS[col.omitted_reason] ?? col.omitted_reason) : undefined}>非表示</span>
                                : formatViewColumnValue(col.values[viewColumnTargetKeyOf(row)] ?? null, col.format_hint)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* <768px: カード縦積み (U-10、7列のためドリル不要) */}
          <div className="md:hidden space-y-3">
            {rows.map((row) => {
              const key = row.employee_id ?? row.external_person_id ?? '';
              return (
                <div key={key} className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm min-w-0 flex-1 truncate">{row.display_name}</span>
                    <span className="text-xs whitespace-nowrap shrink-0"><StatusBadge status={row.status} /></span>
                  </div>
                  {row.determinable && row.actual && row.forecast ? (
                    <>
                      <div className="space-y-1.5">
                        <div>
                          <p className="text-xs font-semibold text-text-body">実績</p>
                          <dl className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex flex-col"><dt className="text-text-sub">月間法定外</dt><dd>{fmtMinutes(row.actual.overtime_minutes_month)}</dd></div>
                            <div className="flex flex-col"><dt className="text-text-sub">年間法定外</dt><dd>{fmtMinutes(row.actual.overtime_minutes_year)}</dd></div>
                            <div className="flex flex-col"><dt className="text-text-sub">深夜労働</dt><dd>{fmtMinutes(row.actual.night_minutes_month)}</dd></div>
                            <div className="flex flex-col">
                              <dt className="text-text-sub">法定休日労働</dt>
                              <dd>{row.actual.holiday_work_determinable === false ? '未特定' : fmtMinutes(row.actual.holiday_work_minutes_month)}</dd>
                            </div>
                          </dl>
                        </div>
                        <div className="pt-1.5 border-t border-surface-border">
                          <p className="text-xs font-semibold text-text-body">見込み</p>
                          <dl className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex flex-col"><dt className="text-text-sub">月間法定外</dt><dd>{fmtMinutes(row.forecast.overtime_minutes_month)}</dd></div>
                            <div className="flex flex-col"><dt className="text-text-sub">年間法定外</dt><dd>{fmtMinutes(row.forecast.overtime_minutes_year)}</dd></div>
                            <div className="flex flex-col"><dt className="text-text-sub">深夜労働</dt><dd>{fmtMinutes(row.forecast.night_minutes_month)}</dd></div>
                            <div className="flex flex-col">
                              <dt className="text-text-sub">法定休日労働</dt>
                              <dd>{row.forecast.holiday_work_determinable === false ? '未特定' : fmtMinutes(row.forecast.holiday_work_minutes_month)}</dd>
                            </div>
                          </dl>
                        </div>
                      </div>
                      <div className="text-xs pt-1.5 border-t border-surface-border"><AgreementCell agreement={row.agreement} /></div>
                    </>
                  ) : (
                    <p className="text-xs text-text-sub">{UNDETERMINABLE_REASON_LABELS[row.undeterminable_reason ?? 'no_attendance_settings']}</p>
                  )}
                  {/* design-expert Major指摘: デスクトップは determinable=false の行でも追加列を「—」で表示するのに
                      モバイルだけ丸ごと消えていた (ブレークポイント間の不整合)。デスクトップと同じ表示に揃える。 */}
                  {(data.view_columns ?? []).length > 0 && (
                    <dl className="grid grid-cols-2 gap-2 text-xs pt-1 border-t border-surface-border">
                      {(data.view_columns ?? []).map((col) => (
                        <div key={col.column_id} className="flex flex-col">
                          <dt className="text-text-sub">{resolveViewColumnLabel(t, col)}</dt>
                          <dd>
                            {!row.determinable
                              ? <span className="text-text-sub">—</span>
                              : col.omitted
                                ? <span title={col.omitted_reason ? (OMITTED_REASON_LABELS[col.omitted_reason] ?? col.omitted_reason) : undefined}>非表示</span>
                                : formatViewColumnValue(col.values[viewColumnTargetKeyOf(row)] ?? null, col.format_hint)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
